/**
 * Variant Selection Helpers
 *
 * Shared logic for selecting a product variant within a product group, respecting
 * the group's checkbox/radio semantics. A variant product is represented in
 * `selectedProducts` by the chosen variant's product ID — the parent ID is never added.
 *
 * @package
 */

/**
 * Find which variant of a product (if any) is currently selected.
 *
 * @param {Array} variants         The product's variants.
 * @param {Array} selectedProducts Currently selected product IDs.
 * @return {number|null} The selected variant ID, or null.
 */
export const getActiveVariantId = ( variants, selectedProducts ) => {
	if ( ! Array.isArray( variants ) || variants.length === 0 ) {
		return null;
	}
	const ids = variants.map( v => v.id );
	return selectedProducts.find( id => ids.includes( id ) ) ?? null;
};

/**
 * Whether a product counts as selected — directly, or via one of its variants.
 *
 * @param {number} productId        The parent product ID.
 * @param {Object} variantsMap      Variants keyed by product ID.
 * @param {Array}  selectedProducts Currently selected product IDs.
 * @return {boolean} True if the product or one of its variants is selected.
 */
export const isProductSelected = ( productId, variantsMap, selectedProducts ) => {
	const variants = variantsMap[ productId ] || [];
	return variants.length > 0
		? getActiveVariantId( variants, selectedProducts ) !== null
		: selectedProducts.includes( productId );
};

/**
 * Build the toggle/select handlers for a product group.
 *
 * @param {Object}   params
 * @param {string}   params.selectionType       'checkbox' or 'radio'.
 * @param {Array}    params.groupProductIds     Parent product IDs in the group.
 * @param {Array}    params.groupVariantIds     All variant IDs across the group's products.
 * @param {Array}    params.selectedProducts    Currently selected product IDs.
 * @param {Function} params.setSelectedProducts Setter.
 * @return {Object} { toggleProduct, selectVariant }
 */
export const makeGroupSelectionHandlers = ( {
	selectionType,
	groupProductIds,
	groupVariantIds,
	selectedProducts,
	setSelectedProducts,
} ) => {
	const clearGroup = arr =>
		arr.filter( id => ! groupProductIds.includes( id ) && ! groupVariantIds.includes( id ) );

	const toggleProduct = productId => {
		if ( selectionType === 'radio' ) {
			if ( selectedProducts.includes( productId ) ) {
				setSelectedProducts( clearGroup( selectedProducts ) );
			} else {
				setSelectedProducts( [ ...clearGroup( selectedProducts ), productId ] );
			}
		} else if ( selectedProducts.includes( productId ) ) {
			setSelectedProducts( selectedProducts.filter( id => id !== productId ) );
		} else {
			setSelectedProducts( [ ...selectedProducts, productId ] );
		}
	};

	const selectVariant = ( variantId, siblingVariantIds ) => {
		const isSelected = selectedProducts.includes( variantId );

		if ( selectionType === 'radio' ) {
			if ( isSelected ) {
				setSelectedProducts( clearGroup( selectedProducts ) );
			} else {
				setSelectedProducts( [ ...clearGroup( selectedProducts ), variantId ] );
			}
			return;
		}

		// Checkbox: a product can hold at most one of its own variants.
		const withoutSiblings = selectedProducts.filter( id => ! siblingVariantIds.includes( id ) );
		if ( isSelected ) {
			setSelectedProducts( withoutSiblings.filter( id => id !== variantId ) );
		} else {
			setSelectedProducts( [ ...withoutSiblings, variantId ] );
		}
	};

	return { toggleProduct, selectVariant };
};
